import { v4 as uuidv4 } from 'uuid';
import type {
  N8nWorkflow,
  N8nNode,
  TransformResult,
  ImportGap,
  EwaNode,
  EwaEdge,
  TransformStats,
} from './types';
import { getEwaNodeType } from './node-mapping';

/**
 * Transform an n8n workflow to EWA format
 */
export function transformN8nToEwa(n8nWorkflow: N8nWorkflow): TransformResult {
  const sessionId = uuidv4();
  const gaps: ImportGap[] = [];
  const warnings: string[] = [];
  let mappedNodes = 0;
  let placeholderNodes = 0;
  let parameterGaps = 0;

  // Build node name to ID lookup (n8n uses names in connections)
  const nodeNameToId = new Map<string, string>();
  n8nWorkflow.nodes.forEach(node => {
    nodeNameToId.set(node.name, node.id);
  });

  // Transform nodes
  const nodes: EwaNode[] = n8nWorkflow.nodes.map(n8nNode => {
    const mapping = getEwaNodeType(n8nNode.type);

    if (!mapping) {
      // Create placeholder no-op node
      placeholderNodes++;
      const placeholderId = n8nNode.id;

      gaps.push({
        gapType: 'UnsupportedNode',
        sourceNodeType: n8nNode.type,
        sourceNodeName: n8nNode.name,
        description: `n8n node type "${n8nNode.type}" is not yet supported in EWA`,
        originalConfig: n8nNode,
        placeholderNodeId: placeholderId,
      });

      return createPlaceholderNode(n8nNode);
    }

    // Transform supported node
    mappedNodes++;
    let parameters: Record<string, unknown> = {};

    if (mapping.parameterTransform) {
      const result = mapping.parameterTransform(n8nNode.parameters);
      parameters = result.parameters;

      // Log parameter-level gaps
      result.unsupportedParams.forEach(up => {
        parameterGaps++;
        gaps.push({
          gapType: 'UnsupportedParameter',
          sourceNodeType: n8nNode.type,
          sourceNodeName: n8nNode.name,
          parameterName: up.name,
          parameterValue: up.value,
          description: up.reason,
          originalConfig: { parameter: up.name, value: up.value },
        });
      });
    } else {
      // Pass through parameters as-is
      parameters = n8nNode.parameters;
    }

    return {
      id: n8nNode.id,
      type: mapping.ewaType,
      position: {
        x: n8nNode.position[0],
        y: n8nNode.position[1],
      },
      data: {
        label: n8nNode.name,
        parameters,
        ...(n8nNode.disabled && { disabled: true }),
      },
    };
  });

  // Build node name to type lookup for edge handle mapping
  const nodeNameToType = new Map<string, string>();
  n8nWorkflow.nodes.forEach(node => {
    nodeNameToType.set(node.name, node.type);
  });

  // Transform connections to edges
  const edges = transformConnections(n8nWorkflow.connections, nodeNameToId, nodeNameToType);

  // Check for credentials (we don't import them)
  const hasCredentials = n8nWorkflow.nodes.some(n => n.credentials && Object.keys(n.credentials).length > 0);
  if (hasCredentials) {
    warnings.push('Credentials were not imported. Please configure credentials manually for each node that requires them.');
  }

  const stats: TransformStats = {
    totalNodes: n8nWorkflow.nodes.length,
    mappedNodes,
    placeholderNodes,
    parameterGaps,
    totalEdges: edges.length,
  };

  return {
    workflow: {
      nodes,
      edges,
      settings: {
        executionOrder: 'v0',
        timezone: 'UTC',
      },
    },
    gaps,
    warnings,
    stats,
    sessionId,
  };
}

/**
 * Create a placeholder no-op node for unsupported n8n nodes
 */
function createPlaceholderNode(n8nNode: N8nNode): EwaNode {
  // Extract short type name for display
  const shortType = n8nNode.type
    .replace('n8n-nodes-base.', '')
    .replace('@n8n/n8n-nodes-langchain.', '');

  return {
    id: n8nNode.id,
    type: 'no-op',
    position: {
      x: n8nNode.position[0],
      y: n8nNode.position[1],
    },
    data: {
      label: `⚠️ ${n8nNode.name} (Unsupported: ${shortType})`,
      parameters: {
        _originalType: n8nNode.type,
        _originalName: n8nNode.name,
        _importedFrom: 'n8n',
        _note: 'This node type is not yet supported. Replace with an equivalent EWA node.',
      },
      ...(n8nNode.disabled && { disabled: true }),
    },
  };
}

/**
 * Mapping of n8n node types to their EWA output handle names
 * EWA uses the output's 'name' property as the handle ID
 * Most nodes use 'main' as their primary output handle name
 */
const OUTPUT_HANDLE_MAPPING: Record<string, string[]> = {
  'n8n-nodes-base.splitInBatches': ['done', 'loop'],
  'n8n-nodes-base.if': ['true', 'false'],
};

/**
 * Get the sourceHandle name for a given node type and output index
 * EWA nodes typically use 'main' as the handle ID for their primary output
 */
function getSourceHandle(n8nNodeType: string, outputIndex: number): string {
  const handles = OUTPUT_HANDLE_MAPPING[n8nNodeType];
  if (handles && handles[outputIndex]) {
    return handles[outputIndex];
  }
  // EWA uses 'main' as the default output handle name (matching node definition output.name)
  return outputIndex === 0 ? 'main' : `output-${outputIndex}`;
}

/**
 * Mapping from n8n AI connection types to EWA anchor/handle IDs
 * n8n uses ai_* prefixed types, EWA uses subNodeCategory names
 */
const N8N_TO_EWA_AI_HANDLE_MAP: Record<string, { anchor: string; targetHandle: string }> = {
  'ai_languageModel': { anchor: 'chatModel', targetHandle: 'subNodeInput' },
  'ai_outputParser': { anchor: 'parser', targetHandle: 'subNodeInput' },
  'ai_tool': { anchor: 'tools', targetHandle: 'default' },
  'ai_memory': { anchor: 'memory', targetHandle: 'subNodeInput' },
  'ai_vectorStore': { anchor: 'vectorStore', targetHandle: 'subNodeInput' },
  'ai_embedding': { anchor: 'embedding', targetHandle: 'subNodeInput' },
  'ai_document': { anchor: 'document', targetHandle: 'subNodeInput' },
  'ai_textSplitter': { anchor: 'textSplitter', targetHandle: 'subNodeInput' },
  'ai_retriever': { anchor: 'retriever', targetHandle: 'subNodeInput' },
};

/**
 * Transform n8n connections (by node name) to EWA edges (by node ID)
 * Handles both standard 'main' connections and AI-specific connection types
 *
 * IMPORTANT: n8n and EWA have OPPOSITE edge directions for AI sub-nodes:
 * - n8n: sub-node (source) → agent (target)
 * - EWA: agent (source) → sub-node (target)
 *
 * So for AI connections, we flip the source and target.
 */
function transformConnections(
  connections: Record<string, Record<string, Array<Array<{ node: string; type: string; index: number }>>>>,
  nodeNameToId: Map<string, string>,
  nodeNameToType: Map<string, string>
): EwaEdge[] {
  const edges: EwaEdge[] = [];

  for (const [sourceName, connectionTypes] of Object.entries(connections)) {
    const n8nSourceId = nodeNameToId.get(sourceName);
    const sourceType = nodeNameToType.get(sourceName);
    if (!n8nSourceId) continue;

    // Process all connection types (main, ai_outputParser, ai_languageModel, etc.)
    for (const [connectionType, outputArrays] of Object.entries(connectionTypes)) {
      if (!Array.isArray(outputArrays)) continue;

      outputArrays.forEach((outputs, outputIndex) => {
        if (!Array.isArray(outputs)) return;

        outputs.forEach(target => {
          const n8nTargetId = nodeNameToId.get(target.node);
          if (!n8nTargetId) return;

          if (connectionType === 'main') {
            // Standard main connections - keep same direction
            // EWA uses 'main' as the handle ID for both inputs and outputs
            edges.push({
              id: uuidv4(),
              source: n8nSourceId,
              target: n8nTargetId,
              sourceHandle: getSourceHandle(sourceType || '', outputIndex),
              targetHandle: 'main',
            });
          } else if (connectionType in N8N_TO_EWA_AI_HANDLE_MAP) {
            // AI connections - FLIP the direction!
            // In n8n: sub-node → agent
            // In EWA: agent → sub-node
            const mapping = N8N_TO_EWA_AI_HANDLE_MAP[connectionType];
            edges.push({
              id: uuidv4(),
              source: n8nTargetId,      // The agent (was n8n target) becomes EWA source
              target: n8nSourceId,      // The sub-node (was n8n source) becomes EWA target
              sourceHandle: mapping.anchor,
              targetHandle: mapping.targetHandle,
            });
          } else {
            // Unknown connection type - keep as-is
            edges.push({
              id: uuidv4(),
              source: n8nSourceId,
              target: n8nTargetId,
              sourceHandle: connectionType,
              targetHandle: connectionType,
            });
          }
        });
      });
    }
  }

  return edges;
}
